import {
  type Camera,
  Raycaster,
  type SkinnedMesh,
  Vector2,
  Vector3
} from 'three'

/**
 * Click-to-paint weight tool for skinned meshes.
 *
 * v1 scope: single-click vertex operation (no continuous brush stroke, no
 * radius/falloff). Two modes:
 *  - paint: assigns the active bone full weight at the clicked vertex,
 *    redistributing other slots to keep the per-vertex weight sum at 1.0.
 *  - smooth: averages the clicked vertex's weights with its N nearest
 *    spatial neighbours (also via raycast hit triangle expansion + a small
 *    KD-walk through `position` attribute distances).
 *
 * Why no brush radius yet: a true paintbrush needs continuous mouse-drag
 * sampling, falloff math, and undo coalescing. The single-vertex MVP gets
 * the plumbing right (raycast → vertex → mutate skinIndex/skinWeight →
 * re-upload to GPU) and is itself useful for fixing the one bad vertex that
 * the influence inspector tells you about. Brush radius is a future addition
 * on top of this same foundation.
 */

export type WeightPaintMode = 'paint' | 'smooth'

export interface WeightPaintToolOptions {
  canvas: HTMLCanvasElement
  camera: Camera
  /** Active bone index (-1 disables paint operations). */
  get_active_bone_index: () => number
  /** Returns the SkinnedMesh(es) the user is allowed to paint on. */
  get_meshes: () => SkinnedMesh[]
}

export class WeightPaintTool {
  private active: boolean = false
  private mode: WeightPaintMode = 'paint'
  private readonly raycaster = new Raycaster()
  private readonly mouse = new Vector2()
  private readonly mousedown_handler: (e: MouseEvent) => void

  constructor (private readonly options: WeightPaintToolOptions) {
    this.mousedown_handler = (e) => this.handle_mousedown(e)
  }

  public is_active (): boolean { return this.active }
  public get_mode (): WeightPaintMode { return this.mode }
  public set_mode (mode: WeightPaintMode): void { this.mode = mode }

  public set_active (active: boolean): void {
    if (this.active === active) return
    this.active = active
    if (active) {
      this.options.canvas.addEventListener('mousedown', this.mousedown_handler, true)
      this.options.canvas.style.cursor = 'crosshair'
    } else {
      this.options.canvas.removeEventListener('mousedown', this.mousedown_handler, true)
      this.options.canvas.style.cursor = ''
    }
  }

  private handle_mousedown (e: MouseEvent): void {
    if (e.button !== 0) return
    const meshes = this.options.get_meshes()
    if (meshes.length === 0) return
    const rect = this.options.canvas.getBoundingClientRect()
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    this.raycaster.setFromCamera(this.mouse, this.options.camera)
    const hits = this.raycaster.intersectObjects(meshes, false)
    if (hits.length === 0) return
    const hit = hits[0]
    const mesh = hit.object as SkinnedMesh
    const face = hit.face
    if (face === null || face === undefined) return
    e.stopPropagation()

    // Closest vertex of the hit triangle (in world space) to the hit point.
    const pos = mesh.geometry.attributes.position
    if (pos === undefined) return
    let best_vi = face.a
    let best_dsq = Infinity
    for (const vi of [face.a, face.b, face.c]) {
      const v = new Vector3().fromBufferAttribute(pos, vi).applyMatrix4(mesh.matrixWorld)
      const dsq = v.distanceToSquared(hit.point)
      if (dsq < best_dsq) { best_dsq = dsq; best_vi = vi }
    }

    if (this.mode === 'paint') {
      const bone_index = this.options.get_active_bone_index()
      if (bone_index < 0) return
      WeightPainter.paint_vertex(mesh, best_vi, bone_index)
    } else {
      WeightPainter.smooth_vertex(mesh, best_vi, 6)
    }
  }
}

/**
 * Pure (no DOM/raycast) weight mutations on a skinned mesh's geometry.
 * Separated from the click tool so it's easy to test and to reuse from a
 * future continuous-brush implementation that wants to call these per
 * sample.
 */
export class WeightPainter {
  /**
   * Set the given bone's weight at `vertex_index` to 1.0, redistributing
   * the other 3 slots to remaining bones (or zero) so the per-vertex weight
   * sum stays 1.0. If the bone already has a slot it's promoted; if not, it
   * replaces the slot with the smallest current weight.
   */
  static paint_vertex (mesh: SkinnedMesh, vertex_index: number, bone_index: number): void {
    const indices = mesh.geometry.attributes.skinIndex
    const weights = mesh.geometry.attributes.skinWeight
    if (indices === undefined || weights === undefined) return

    let target_slot = -1
    let min_weight = Infinity
    let min_slot = 0
    for (let k = 0; k < 4; k++) {
      const bi = indices.getComponent(vertex_index, k)
      const w = weights.getComponent(vertex_index, k)
      if (bi === bone_index) { target_slot = k; break }
      if (w < min_weight) { min_weight = w; min_slot = k }
    }
    if (target_slot === -1) target_slot = min_slot

    // Set target slot to bone with full weight; zero the rest. Hard-paint
    // semantics — the user clicked, they want this bone to own the vertex.
    for (let k = 0; k < 4; k++) {
      const w = k === target_slot ? 1 : 0
      const bi = k === target_slot ? bone_index : 0
      set_component(weights, vertex_index, k, w)
      set_component(indices, vertex_index, k, bi)
    }
    indices.needsUpdate = true
    weights.needsUpdate = true
  }

  /**
   * Average the weights at `vertex_index` with `neighbor_count` of its
   * spatial nearest neighbours. Picks neighbours by raw L2 distance over
   * the geometry's `position` attribute. O(V * neighbor_count) per call,
   * which is fine for occasional clicks; not suitable for a continuous
   * brush stroke yet.
   *
   * Implementation note: smoothing in skinning means averaging a sparse
   * representation (4-bone weight slots). We aggregate per-bone-index
   * weights across the neighbour set, then keep the top-4 and renormalize.
   */
  static smooth_vertex (mesh: SkinnedMesh, vertex_index: number, neighbor_count: number): void {
    const pos = mesh.geometry.attributes.position
    const indices = mesh.geometry.attributes.skinIndex
    const weights = mesh.geometry.attributes.skinWeight
    if (pos === undefined || indices === undefined || weights === undefined) return

    const target = new Vector3().fromBufferAttribute(pos, vertex_index)
    const distances: Array<{ vi: number, dsq: number }> = []
    const v = new Vector3()
    for (let i = 0; i < pos.count; i++) {
      if (i === vertex_index) continue
      v.fromBufferAttribute(pos, i)
      distances.push({ vi: i, dsq: target.distanceToSquared(v) })
    }
    distances.sort((a, b) => a.dsq - b.dsq)
    const neighbours = distances.slice(0, neighbor_count).map(d => d.vi)
    neighbours.push(vertex_index)

    const accumulated = new Map<number, number>()
    for (const vi of neighbours) {
      for (let k = 0; k < 4; k++) {
        const bi = indices.getComponent(vi, k)
        const w = weights.getComponent(vi, k)
        if (w <= 0) continue
        accumulated.set(bi, (accumulated.get(bi) ?? 0) + w)
      }
    }
    if (accumulated.size === 0) return
    const top4 = Array.from(accumulated.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
    const total = top4.reduce((s, [, w]) => s + w, 0)
    for (let k = 0; k < 4; k++) {
      const entry = top4[k]
      const bi = entry?.[0] ?? 0
      const w = entry !== undefined ? entry[1] / total : 0
      set_component(weights, vertex_index, k, w)
      set_component(indices, vertex_index, k, bi)
    }
    indices.needsUpdate = true
    weights.needsUpdate = true
  }
}

/**
 * Set a single component of a 4-component buffer attribute. Three.js's
 * BufferAttribute API exposes setX/setY/setZ/setW but doesn't have a generic
 * `setComponent(index, component, value)` — this just routes to the right
 * setter so callers can write a small loop instead of a switch.
 */
function set_component (
  attr: { setX: (i: number, v: number) => void, setY: (i: number, v: number) => void, setZ: (i: number, v: number) => void, setW: (i: number, v: number) => void },
  index: number,
  component: number,
  value: number
): void {
  if (component === 0) attr.setX(index, value)
  else if (component === 1) attr.setY(index, value)
  else if (component === 2) attr.setZ(index, value)
  else if (component === 3) attr.setW(index, value)
}
